import { useContext, useEffect, useState } from 'react';
import { useLoaderData, useNavigate } from 'react-router-dom';
import { useImmerReducer } from 'use-immer';
import { GlobalContext } from '../../../library/Core/GlobalContext';
import { SmartContext } from '../../../library/Core/SmartContext';
import SessionContext from '../../../library/Core/SessionContext';
import smartReducer from '../../../library/Core/SmartReducer';
import { DispatchEvent, State } from '../../../library/Core/SmartTypes';
import DebugBarControl from '../../../library/SimpleControls/DebugBarControl';
import Layout from './Layout';
import { addUserSpecificDomains } from '../../../library/Service/domainService';
import LoadingSpinner from '../../../library/SimpleControls/LoadingSpinner';
import AddServiceProviderModal from './AddServiceProviderModal';

const BusinessDirectory = () => {
    const { globalState } = useContext(GlobalContext);
    const { sessionState } = useContext(SessionContext);
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('approved');

    const [state, dispatch] = useImmerReducer<State, DispatchEvent>(smartReducer, {
        flags: { isDataLoading: true, showFormErrors: 0 },
        formValidationErrors: {},
        actions: {},
    } as any);

    const { formConfig, data, internal, routeInfo } = useLoaderData() as State;
    const domain = addUserSpecificDomains(new Map([...globalState.domain]), data);

    useEffect(() => {
        // Apply filter domains to the domain map
        if (data?.filterDomains && typeof data.filterDomains === 'object') {
            applyFilterDomains({ filterDomains: data.filterDomains, formConfig, domain });
        }

        dispatch({
            type: 'DATA_INIT',
            payload: { formConfig, data: { ...data, userTypeCode: sessionState?.userTypeCode }, domain, internal, routeInfo },
        });
    }, []);

    function applyFilterDomains({
        filterDomains,
        formConfig,
        domain,
    }: {
        filterDomains: Record<string, any[]>;
        formConfig: any;
        domain: Map<string, any[]>;
    }) {
        const allFormItems =
            formConfig?.sectionRepository?.flatMap((section: any) => section.controlGroup || []) || [];

        Object.entries(filterDomains).forEach(([key, filterValue]) => {
            if (!Array.isArray(filterValue)) {
                console.warn(`Skipping domain key "${key}" as its value is not an array:`, filterValue);
                return;
            }

            const formItem = allFormItems.find((item: any) => item?.props?.domainCategoryCode === key);

            if (!formItem) {
                console.warn(`No form item found with domainCategoryCode: ${key}`);
                domain.set(key, filterValue);
                return;
            }

            const domainCategoryCode = formItem.props.domainCategoryCode;
            domain.set(domainCategoryCode, filterValue);
        });
    }

    const handleTabChange = (tab: 'approved' | 'suggested') => {
        if (tab === 'suggested') {
            navigate('/support/business-directory/ai-suggested');
            return;
        }
        setActiveTab(tab);
    };

    const isAlumni = sessionState?.userTypeCode === 'ALUMNI';
    const canViewAISuggested = !isAlumni;

    return (
        <SmartContext.Provider value={{ state, dispatch }}>
            <div className="main flex-1">
                {!state?.data && <LoadingSpinner />}
                {state?.data && (
                    <div className="bg-white">
                        <div className="py-5">
                            <div className="max-1140 mx-auto">
                                <div className="">
                                    <div className="business-directory">
                                        <div className="row g-2">
                                            <div className="col">
                                                <h2 className="font-26 font-600 mb-0 pb-4">{state.formConfig?.title}</h2>
                                            </div>

                                            <div className="col-auto">
                                                {!isAlumni && (
                                                    <div className="dashboard innerpage smart-scroll scrolled-down">
                                                        <AddServiceProviderModal className="btn btn-green px-3 text-decoration-none" modalTitle='Add Service Provider'/>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Tabs */}
                                        <div className="tabs-container mb-4">
                                            <ul className="nav nav-pills business-directory-tab mb-4 pb-2" role="tablist">
                                                <li className="nav-item" role="presentation">
                                                    <button
                                                        className={`nav-link ${activeTab === 'approved' ? 'active' : ''}`}
                                                        onClick={() => handleTabChange('approved')}
                                                        type="button"
                                                        role="tab"
                                                        aria-selected={activeTab === 'approved'}
                                                    >
                                                        Approved Directory 
                                                    </button>
                                                </li>
                                                {canViewAISuggested && (
                                                    <li className="nav-item" role="presentation">
                                                        <button
                                                            className={`nav-link ${activeTab === 'suggested' ? 'active' : ''}`}
                                                            onClick={() => handleTabChange('suggested')}
                                                            type="button"
                                                            role="tab"
                                                            aria-selected={activeTab === 'suggested'}
                                                        >
                                                           Recommended
                                                        </button>
                                                    </li>
                                                )}
                                            </ul>
                                        </div>

                                        {/* Tab Content */}
                                        {activeTab === 'approved' && <Layout />}
                                        {activeTab === 'suggested' && canViewAISuggested && <Layout />}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                
                <DebugBarControl />
            </div>
        </SmartContext.Provider>
    );
};

export default BusinessDirectory;

